import { getActiveTab, isMediumDevice as isMediumDeviceSelector, isTabletDevice } from '@va/dashboard/selectors/ui';
import { PauseIcon, PlayIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { Button, Paragraph, fontWeights, paragraphSizes } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import classNames from 'classnames';
import { MouseEvent, useState } from 'react';
import { useSelector } from 'react-redux';

type FeatureToggleProps = {
  active: boolean;
  disabled: boolean;
  onClick: (e: MouseEvent) => Promise<void> | void;
  disabledTooltipText?: string;
  'data-testid'?: string;
};

export function FeatureToggleButton({
  disabled,
  active,
  onClick,
  disabledTooltipText,
  'data-testid': dataTestId,
}: FeatureToggleProps) {
  const [inProgress, setInProgress] = useState(false);
  const translate = useTranslate();
  const isTablet = useSelector(isTabletDevice);
  const isMediumDevice = useSelector(isMediumDeviceSelector);

  const handleClick = async (e: MouseEvent) => {
    setInProgress(true);
    await onClick(e);
    setInProgress(false);
  };

  return (
    <TooltipWrapper
      interactive
      placement='bottom'
      content={disabled ? <div>{disabledTooltipText}</div> : <TooltipContent active={active} />}
      tooltipClassNames={classNames({ 'rounded-18!': !disabled })}
      disabled={isTablet}
    >
      <div>
        <Button
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            handleClick(e);
          }}
          disabled={inProgress || disabled}
          className={classNames({
            'bg-green-apple text-green-pure': active,
            'text-red-negative': !active,
          })}
          text={active ? translate('featureToggle.status.active') : translate('featureToggle.status.paused')}
          color='secondary'
          icon={() =>
            active ? (
              <PlayIcon color='var(--color-primary)' className='h-6 w-6 sm:w-18px sm:h-18px' />
            ) : (
              <PauseIcon color='red' className='h-6 w-6 sm:w-18px sm:h-18px' />
            )
          }
          data-testid={dataTestId}
          size={isMediumDevice ? 'small' : 'large'}
        />
      </div>
    </TooltipWrapper>
  );
}

const TooltipContent: React.FC<{ active: boolean }> = ({ active }) => {
  const activeTab = useSelector(getActiveTab);
  const translate = useTranslate();

  return (
    <div className='flex flex-col w-75 py-2 px-1'>
      <div
        className={classNames('flex justify-center items-center p-2 rounded-12 h-12 text-white', {
          'bg-green-pure': active,
          'bg-red-negative': !active,
        })}
      >
        <Paragraph weight={fontWeights.medium}>
          {translate('featureToggle.featureStatus', {
            feature: translate(`tab.${activeTab}`),
            status: active ? translate('featureToggle.status.active') : translate('featureToggle.status.paused'),
          })}
        </Paragraph>
      </div>
      <Paragraph className='mt-9px flex justify-center' size={paragraphSizes.tiny} weight={fontWeights.medium}>
        {translate('featureToggle.pressAgain', {
          feature: translate(`tab.${activeTab}`),
          status: active ? translate('featureToggle.status.pause') : translate('featureToggle.status.activate'),
        })}
      </Paragraph>
    </div>
  );
};
